import React, { useMemo, useState } from "react";
import { Accordion, Button, Card, Form } from "react-bootstrap";
import { DebounceInput } from "react-debounce-input";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import { dateFilter, selectorFilter } from "../../../shared/crud/filter-widgets";
import {
  Chevron,
  CustomCardTitle,
  StyledCard,
  StyledCardDeck,
  StyledSimpleRow,
  TitleChevronContainer,
} from "../../../shared/crud/list";
import {
  ExperimentFilters as ExperimentFiltersValue,
  ExperimentStatus,
} from "../domain/experiment.entity";
import { messages } from "../experiment.messages";

const NAME_DEBOUNCE_MS = 300;
const DURATION_DEBOUNCE_MS = 300;

const StyledFilterNameInput = styled(Form.Control)`
  width: 320px;
`;

const StyledFilterMinDaysInput = styled(Form.Control)`
  width: 200px;
`;

export interface ExperimentFiltersProps {
  value: ExperimentFiltersValue;
  onChange: (next: ExperimentFiltersValue) => void;
  closedByDefault?: boolean;
}

interface DateRangeWidgetValue {
  startDate: string | null;
  endDate: string | null;
}

export function ExperimentFilters({
  value,
  onChange,
  closedByDefault = true,
}: ExperimentFiltersProps) {
  const intl = useIntl();
  const [toggleClosed, setToggleClosed] = useState(closedByDefault);
  const [focused, setFocused] = useState<string | undefined>();

  // `calendarDay`: see the note in ExperimentEvolutionChart — without it the widget shifts the
  // picked day back by one east of UTC, so the filter would query the wrong start day.
  const DateRangeWidget = useMemo(
    () => dateFilter("experimentStartDate", intl, { calendarDay: true }),
    [intl]
  );

  const dateRangeValue: DateRangeWidgetValue = {
    startDate: value.startedAtFrom ? value.startedAtFrom.toISOString().slice(0, 10) : null,
    endDate: value.startedAtTo ? value.startedAtTo.toISOString().slice(0, 10) : null,
  };

  const statusOptions = useMemo(
    () => [
      { value: "", label: intl.formatMessage(messages.filters.statusAll) },
      {
        value: ExperimentStatus.EXPERIMENT_STATUS_RUNNING.toString(),
        label: intl.formatMessage(messages.status.running),
      },
      {
        value: ExperimentStatus.EXPERIMENT_STATUS_ARCHIVED.toString(),
        label: intl.formatMessage(messages.status.archived),
      },
      {
        value: ExperimentStatus.EXPERIMENT_STATUS_PAUSED.toString(),
        label: intl.formatMessage(messages.status.paused),
      },
      {
        value: ExperimentStatus.EXPERIMENT_STATUS_DRAFT.toString(),
        label: intl.formatMessage(messages.filters.statusIniciar),
      },
    ],
    [intl]
  );

  // Reuses the same shared factory the reservation module's list filters build on
  // (filter-widgets.tsx's `selectorFilter`), so the status combo gets the same comfortable,
  // fixed 240px width instead of shrinking to its CardDeck column's share.
  const StatusWidget = useMemo(
    () => selectorFilter(intl.formatMessage(messages.filters.statusPlaceholder), statusOptions),
    [intl, statusOptions]
  );

  const statusValue = value.status !== undefined ? value.status.toString() : "";

  return (
    <Accordion defaultActiveKey={closedByDefault ? undefined : "0"}>
      <Card>
        <Card.Body>
          <Accordion.Toggle
            as={CustomCardTitle}
            variant="link"
            eventKey="0"
            onClick={() => setToggleClosed(!toggleClosed)}>
            <TitleChevronContainer>
              <span>
                <FormattedMessage {...messages.filters.title} />
              </span>
              <Chevron toggleClosed={toggleClosed} />
            </TitleChevronContainer>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <>
              <StyledCardDeck>
                <StyledSimpleRow>
                  <StyledCard>
                    <DebounceInput
                      element={StyledFilterNameInput as any}
                      minLength={0}
                      debounceTimeout={NAME_DEBOUNCE_MS}
                      type="text"
                      value={value.tickerContains || ""}
                      placeholder={intl.formatMessage(messages.filters.namePlaceholder)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onChange({ ...value, tickerContains: e.target.value || undefined })
                      }
                    />
                  </StyledCard>
                </StyledSimpleRow>

                <StyledSimpleRow>
                  <StyledCard>
                    <StatusWidget
                      value={statusValue}
                      onChange={(newValue: string | undefined) =>
                        onChange({
                          ...value,
                          status: newValue ? (Number(newValue) as ExperimentStatus) : undefined,
                        })
                      }
                    />
                  </StyledCard>
                </StyledSimpleRow>

                <StyledSimpleRow>
                  <StyledCard>
                    <DateRangeWidget
                      value={dateRangeValue}
                      onChange={(dates: DateRangeWidgetValue) =>
                        onChange({
                          ...value,
                          startedAtFrom: dates.startDate ? new Date(dates.startDate) : undefined,
                          startedAtTo: dates.endDate ? new Date(dates.endDate) : undefined,
                        })
                      }
                      placeholder={intl.formatMessage(messages.filters.dateRangePlaceholder)}
                      onFocusChange={setFocused}
                      focused={focused}
                    />
                  </StyledCard>
                </StyledSimpleRow>

                <StyledSimpleRow>
                  <StyledCard>
                    <DebounceInput
                      element={StyledFilterMinDaysInput as any}
                      minLength={0}
                      debounceTimeout={DURATION_DEBOUNCE_MS}
                      type="number"
                      min={0}
                      value={value.minDurationDays ?? ""}
                      placeholder={intl.formatMessage(messages.filters.minDurationPlaceholder)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const raw = e.target.value;
                        const parsed = raw === "" ? undefined : Math.max(0, parseInt(raw, 10) || 0);
                        onChange({ ...value, minDurationDays: parsed });
                      }}
                    />
                  </StyledCard>
                </StyledSimpleRow>
              </StyledCardDeck>

              <Button variant="primary" onClick={() => onChange({})}>
                <FormattedMessage {...messages.filters.clear} />
              </Button>
            </>
          </Accordion.Collapse>
        </Card.Body>
      </Card>
    </Accordion>
  );
}
